import { c as _c } from "react/compiler-runtime";
import { animate } from 'framer-motion';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTimeout } from 'usehooks-ts';
import { useUAContext } from '@/context/UAContext';
import { createVelocityTrail } from '@/lib/momentum-scroll';
const DRAG_THRESHOLD = 5;
const CLICK_BLOCK_DURATION = 100;
type DragState = {
  dragging: boolean;
  startX: number;
  scrollStart: number;
  stopMomentum: () => void;
};

/**
 * Adds mouse drag-to-scroll with momentum to a horizontally scrollable container.
 * Only activates on fine-pointer devices (mouse/trackpad); VR browsers and touch
 * devices use native scroll and are left untouched.
 */
export const useDraggingScrollMotion = scrollRef => {
  const $ = _c(6);
  const {
    device
  } = useUAContext();
  const isVR = device === "vr_browser";
  const hasFinePointer = useMediaQuery("(pointer: fine)");
  const enabled = hasFinePointer && !isVR;
  const hasMovedRef = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      dragging: false,
      startX: 0,
      scrollStart: 0,
      stopMomentum: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dragRef = useRef(t0);
  const [isClickBlocking, setIsClickBlocking] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      hasMovedRef.current = false;
      setIsClickBlocking(false);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useTimeout(t1, isClickBlocking ? CLICK_BLOCK_DURATION : null);
  let t2;
  let t3;
  if ($[2] !== enabled || $[3] !== scrollRef) {
    t2 = () => {
      const el = scrollRef.current;
      if (!el || !enabled) {
        return;
      }
      const trail = createVelocityTrail();
      const drag = dragRef.current;
      const onPointerMove = e => {
        if (!drag.dragging) {
          return;
        }
        const dx = e.clientX - drag.startX;
        if (!hasMovedRef.current && Math.abs(dx) > DRAG_THRESHOLD) {
          hasMovedRef.current = true;
          el.style.cursor = "grabbing";
          el.style.userSelect = "none";
        }
        if (hasMovedRef.current) {
          el.scrollLeft = drag.scrollStart - dx;
          trail.record(e.clientX);
        }
      };
      const onPointerUp = () => {
        drag.dragging = false;
        el.style.cursor = "";
        el.style.userSelect = "";
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        setIsClickBlocking(true);
        const v = trail.velocity();
        if (hasMovedRef.current && v !== null) {
          const velocityPxS = -v * 1000;
          const maxScroll = el.scrollWidth - el.clientWidth;
          const projected = el.scrollLeft + velocityPxS * 0.8 * 0.7;
          const target = Math.max(0, Math.min(maxScroll, projected));
          const controls = animate(el.scrollLeft, target, {
            type: "inertia",
            velocity: velocityPxS,
            onUpdate: value => {
              el.scrollLeft = value;
            }
          });
          drag.stopMomentum = controls.stop.bind(controls);
        }
        trail.clear();
      };
      const onPointerDown = e_0 => {
        if (e_0.button !== 0) {
          return;
        }
        drag.stopMomentum();
        setIsClickBlocking(false);
        drag.dragging = true;
        hasMovedRef.current = false;
        drag.startX = e_0.clientX;
        drag.scrollStart = el.scrollLeft;
        trail.clear();
        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
      };
      const onWheel = () => {
        drag.stopMomentum();
      };
      const onDragStart = _temp2;
      const onClick = e_2 => {
        if (!hasMovedRef.current) {
          return;
        }
        if ((e_2.target as HTMLElement).closest("a")) {
          e_2.preventDefault();
          e_2.stopPropagation();
          e_2.stopImmediatePropagation();
        }
      };
      el.addEventListener("pointerdown", onPointerDown);
      el.addEventListener("wheel", onWheel);
      el.addEventListener("dragstart", onDragStart);
      el.addEventListener("click", onClick, {
        capture: true
      });
      return () => {
        drag.stopMomentum();
        el.removeEventListener("pointerdown", onPointerDown);
        el.removeEventListener("wheel", onWheel);
        el.removeEventListener("dragstart", onDragStart);
        el.removeEventListener("click", onClick, {
          capture: true
        });
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };
    };
    t3 = [enabled, scrollRef];
    $[2] = enabled;
    $[3] = scrollRef;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
};
function _temp() {}
function _temp2(e_1) {
  return e_1.preventDefault();
}